import * as Sentry from '@sentry/browser';
import { playbackAPI } from '../../../communication';

/**
 * These events are sent by the host to interact with the reader's controls.
 * When a user interacts with a playback control of the OS,
 * the host sends the event to indicate to the reader what action to perform.
 */
const IN_EVENTS = {
    PLAY: 'externalPlayTrack',
    PAUSE: 'externalPauseTrack',
    NEXT: 'externalNextTrack',
    PREVIOUS: 'externalPreviousTrack',
    SEEK: 'externalSeekToTrackSecond',
};

const mediaSession = {
    isMediaSessionAvailable() {
        return 'mediaSession' in navigator;
    },
    isMediaSessionEnabled() {
        return mediaSession.isMediaSessionAvailable();
    },
    clearMediaSession() {
        if (mediaSession.isMediaSessionAvailable()) {
            try {
                navigator.mediaSession.metadata = null;
                navigator.mediaSession.playbackState = 'none';
                navigator.mediaSession.setActionHandler('play', null);
                navigator.mediaSession.setActionHandler('pause', null);
                navigator.mediaSession.setActionHandler('previoustrack', null);
                navigator.mediaSession.setActionHandler('nexttrack', null);
                navigator.mediaSession.setActionHandler('seekbackward', null);
                navigator.mediaSession.setActionHandler('seekforward', null);
            } catch (error) {
                console.warn('Error clearing media session:', error);
                Sentry.captureException(error);
            }
        }
    },
    setMediaSessionState(state) {
        if (mediaSession.isMediaSessionEnabled()) {
            try {
                navigator.mediaSession.playbackState = state;
            } catch (error) {
                console.warn('Error setting media session state:', error);
                Sentry.captureException(error);
            }
        }
    },
    setMediaSessionPosition({ newSeekPositionInSeconds, duration, rate }) {
        if (!mediaSession.isMediaSessionEnabled()) {
            return;
        }

        const safePosition = Math.min(newSeekPositionInSeconds, duration);
        if (!(safePosition > 0)) {
            return;
        }

        try {
            navigator.mediaSession.setPositionState({
                duration,
                position: safePosition,
                playbackRate: rate,
            });
        } catch (error) {
            console.warn('Error setting media session position:', error);
            Sentry.captureException(error);
        }
    },
    setMediaSessionData({
        author,
        title,
        image,
        track,
        duration,
        onPlay,
        onPause,
        onNext,
        onPrev,
        _onSeekBackward,
        _onSeekForward,
    }) {
        if (mediaSession.isMediaSessionEnabled()) {
            try {
                navigator.mediaSession.metadata = new MediaMetadata({
                    title: `${track.chapter} - ${track.title}`,
                    artist: author,
                    album: title,
                    artwork: [
                        {
                            src: image,
                            sizes: '96x96',
                            type: 'image/png',
                        },
                        {
                            src: image,
                            sizes: '128x128',
                            type: 'image/png',
                        },
                        {
                            src: image,
                            sizes: '192x192',
                            type: 'image/png',
                        },
                        {
                            src: image,
                            sizes: '256x256',
                            type: 'image/png',
                        },
                        {
                            src: image,
                            sizes: '384x384',
                            type: 'image/png',
                        },
                        {
                            src: image,
                            sizes: '512x512',
                            type: 'image/png',
                        },
                    ],
                });

                mediaSession.setMediaSessionState('paused');
                mediaSession.setMediaSessionPosition({ duration });

                // Manejar eventos de MediaSession de manera segura
                const safeSetActionHandler = (action, handler) => {
                    try {
                        navigator.mediaSession.setActionHandler(action, handler);
                    } catch (error) {
                        console.warn(`Error setting ${action} handler:`, error);
                        Sentry.captureException(error);
                    }
                };

                safeSetActionHandler('play', onPlay);
                safeSetActionHandler('pause', onPause);
                safeSetActionHandler('previoustrack', onPrev);
                safeSetActionHandler('nexttrack', onNext);

                // TODO: Unncomment this to enable seek backward and forward
                // safeSetActionHandler('seekbackward', onSeekBackward);
                // safeSetActionHandler('seekforward', onSeekForward);
            } catch (error) {
                console.warn('Error setting media session data:', error);
                Sentry.captureException(error);
                mediaSession.clearMediaSession();
            }
        } else {
            mediaSession.clearMediaSession();
        }
    },
};

export default {
    IN_EVENTS,

    loadAudioTrack(payload) {
        mediaSession.setMediaSessionData(payload);
    },

    playAudioTrack(payload) {
        playbackAPI.playTrack({ trackIndex: payload?.track });
        mediaSession.setMediaSessionState('playing');
    },

    pauseAudioTrack() {
        playbackAPI.pauseTrack();
        mediaSession.setMediaSessionState('paused');
    },

    stopAudioTrack() {
        mediaSession.setMediaSessionState('none');
    },

    nextAudioTrack() {
        playbackAPI.nextTrack();
    },

    previousAudioTrack() {
        playbackAPI.previousTrack();
    },

    seekToAudioTrackSecond(payload) {
        playbackAPI.seekToPosition(payload);
        mediaSession.setMediaSessionPosition(payload);
    },
};
